<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import type { EmitsToListeners } from '@/types/core';

//#region types
interface Props {
  propsPeriod?: any;
}
interface Emits {
  'apply-filters': [value: any],
  'close': []
}

export interface CampaignStatisticFilterParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>;
}
//#endregion

//#region define
const props = defineProps<Props>();
const emit = defineEmits<Emits>();
//#endregion

//#region state 
const isOpen = ref(false);
const period = ref<any[] | undefined>();
//#endregion

//#region computed
const hasFilters = computed(() => {
  return !!props.propsPeriod;
});
//#endregion

//#region methods
function resetFilter() {
  isOpen.value = false;
  emit('apply-filters', null);
}

function applyCampaignStatistics() {
  isOpen.value = false;
  emit('apply-filters', period.value);
}

function popoverCampaignStatisticsTrigger() {
  isOpen.value = !isOpen.value;
}

function escHandler(e: KeyboardEvent) {
  if (isOpen.value && e.key === 'Escape') {
    emit('close');
  }
}

function close() {
  emit('close');
  isOpen.value = false;
}

function handleOutside(e: MouseEvent) {
  const isInsideElPopper = (e.target as Element)?.closest('.el-popper');
  if (!isInsideElPopper && isOpen.value) {
    close();
  }
}
//#endregion

//#region watch 
watch(isOpen, (value) => {
  if (value && props.propsPeriod) {
    const { stats_period_start, stats_period_end } = props.propsPeriod;
    period.value = [stats_period_start, stats_period_end];
  }
});
//#endregion

//#region hooks
onMounted(() => {
  window.addEventListener('keyup', escHandler);
});
onUnmounted(() => {
  window.removeEventListener('keyup', escHandler);
});
//#endregion
</script>

<template>
  <ElPopover
    :visible="isOpen"
    width="auto"
    placement="bottom"
    popper-class="campaign-statistics-filter__popover"
  >
    <template v-slot:reference>
      <UiButton
        v-click-outside="handleOutside"
        :filled="hasFilters"
        icon="filter"
        is-icon
        size="big"
        :is-clear="hasFilters"
        @click="popoverCampaignStatisticsTrigger"
        @clear-click="resetFilter"
      />
    </template>
    <div class="t:space-y-12">
      <UiDatePicker
        v-model="period"
        :label="$t('mediaBuying.statistics.select_period')"
        :placeholder="$t(`tools.match_popup.placeholder_date_time`)"
        :always-clearable="false"
      />
      <div class="t:flex t:justify-end t:space-x-8">
        <UiButton
          type="negative"
          icon="times"
          @click="resetFilter"
        >
          {{ $t('reports.other.reset') }}
        </UiButton>
        <UiButton
          filled
          icon="check"
          @click="applyCampaignStatistics"
        >
          {{ $t('reports.other.apply') }}
        </UiButton>
      </div>
    </div>
  </ElPopover>
</template>

<style lang="scss">
.campaign-statistics-filter {
  &__popover.el-popover.el-popper {
    padding: 16px;
    z-index: 800 !important;
    border-radius: 8px;
    background: var(--background-body);
    box-shadow: 0 4px 32px 0 rgba(0, 0, 0, 0.16);
    min-width: 240px !important;
  }
}
</style>
